import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, Mail, MapPin, Clock3 } from "lucide-react";

import { useLanguage } from "@/context/LanguageContext";
import { media, assets } from "@/lib/media";

import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fieldBase =
  "w-full border-0 border-b border-warmBorder bg-transparent px-0 py-3 text-[15px] text-ocean placeholder:text-mutedText/60 focus:border-terracotta focus:outline-none focus:ring-0 transition-colors";

function Label({ children, htmlFor }) {
  return (
    <label
      htmlFor={htmlFor}
      className="mb-2 block text-[10px] uppercase tracking-[0.26em] text-mutedText"
    >
      {children}
    </label>
  );
}

export default function ContactBooking() {
  const { t, lang } = useLanguage();
  const [tab, setTab] = useState("message");

  // --- Contact form state ---
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [contactLoading, setContactLoading] = useState(false);

  // --- Booking form state ---
  const [bookingForm, setBookingForm] = useState({
    name: "",
    email: "",
    phone: "",
    session_type: "",
    preferred_time: "",
    location: "",
    notes: "",
  });
  const [bookingDate, setBookingDate] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);

  const sessionOptions = [
    { value: "wedding", label: t("contact.form.session_options.wedding") },
    { value: "family", label: t("contact.form.session_options.family") },
    { value: "portrait", label: t("contact.form.session_options.portrait") },
    { value: "christmas", label: t("contact.form.session_options.christmas") },
    { value: "other", label: t("contact.form.session_options.other") },
  ];

  // --- Handlers ---
  const onContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast.error(t("contact.form.required"));
      return;
    }
    try {
      setContactLoading(true);
      await axios.post(`${API_BASE}/contact`, { ...contactForm, language: lang });
      toast.success(t("contact.form.success_message"));
      setContactForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error(t("contact.form.error"));
    } finally {
      setContactLoading(false);
    }
  };

  const onBookingSubmit = async (e) => {
    e.preventDefault();
    if (
      !bookingForm.name ||
      !bookingForm.email ||
      !bookingForm.session_type ||
      !bookingDate
    ) {
      toast.error(t("contact.form.required"));
      return;
    }
    try {
      setBookingLoading(true);
      await axios.post(`${API_BASE}/booking`, {
        ...bookingForm,
        preferred_date: format(bookingDate, "yyyy-MM-dd"),
        language: lang,
      });
      toast.success(t("contact.form.success_booking"));
      setBookingForm({
        name: "",
        email: "",
        phone: "",
        session_type: "",
        preferred_time: "",
        location: "",
        notes: "",
      });
      setBookingDate(null);
    } catch (err) {
      console.error(err);
      toast.error(t("contact.form.error"));
    } finally {
      setBookingLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-ocean text-sand"
    >
      <div className="mx-auto grid max-w-[1600px] grid-cols-1 lg:grid-cols-2">
        {/* Left: emotional image — swaps between tabs with crossfade */}
        <div className="relative hidden min-h-[720px] overflow-hidden lg:block">
          <img
            src={assets.contactMessage}
            alt="Blessed Algarve — sessão de contacto"
            className="absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-out"
            style={{ opacity: tab === "message" ? 1 : 0, objectPosition: "center 35%" }}
          />
          <img
            src={assets.contactBooking}
            alt="Blessed Algarve — sessão de agendamento"
            className="absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-out"
            style={{ opacity: tab === "booking" ? 1 : 0, objectPosition: "center 30%" }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-ocean/60 via-ocean/30 to-ocean/70" />
          <div className="absolute bottom-12 left-12 right-12 max-w-md">
            <p className="overline text-terracotta/90">{t("contact.overline")}</p>
            <p className="mt-4 font-display text-3xl italic leading-tight text-sand">
              “A luz do Algarve é a nossa musa. E você, a história.”
            </p>
            <div className="mt-10 space-y-4 text-sm text-sand/80">
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-terracotta" strokeWidth={1.4} />
                <span>{t("contact.info.email")}</span>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="h-4 w-4 text-terracotta" strokeWidth={1.4} />
                <span>{t("contact.info.location")}</span>
              </div>
              <div className="flex items-center gap-3">
                <Clock3 className="h-4 w-4 text-terracotta" strokeWidth={1.4} />
                <span>
                  {t("contact.info.reply_label")} · {t("contact.info.reply")}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right: form */}
        <div className="px-6 py-20 sm:px-12 lg:px-20 lg:py-28">
          <p className="overline text-terracotta">{t("contact.overline")}</p>
          <h2
            data-testid="contact-title"
            className="mt-4 font-display text-4xl leading-tight text-sand sm:text-5xl lg:text-6xl"
          >
            {t("contact.title")}
          </h2>
          <p className="mt-6 max-w-lg text-sm leading-relaxed text-sand/75">
            {t("contact.subtitle")}
          </p>

          <Tabs
            value={tab}
            onValueChange={setTab}
            className="mt-10"
            data-testid="contact-tabs"
          >
            <TabsList className="flex h-auto w-full gap-6 rounded-none border-b border-sand/20 bg-transparent p-0 text-sand">
              <TabsTrigger
                value="message"
                data-testid="tab-message"
                className="rounded-none border-b-2 border-transparent bg-transparent px-0 pb-3 pt-0 text-[11px] uppercase tracking-[0.26em] text-sand/60 shadow-none data-[state=active]:border-terracotta data-[state=active]:bg-transparent data-[state=active]:text-sand data-[state=active]:shadow-none"
              >
                {t("contact.tabs.message")}
              </TabsTrigger>
              <TabsTrigger
                value="booking"
                data-testid="tab-booking"
                className="rounded-none border-b-2 border-transparent bg-transparent px-0 pb-3 pt-0 text-[11px] uppercase tracking-[0.26em] text-sand/60 shadow-none data-[state=active]:border-terracotta data-[state=active]:bg-transparent data-[state=active]:text-sand data-[state=active]:shadow-none"
              >
                {t("contact.tabs.booking")}
              </TabsTrigger>
            </TabsList>

            {/* Message form */}
            <TabsContent value="message" className="mt-10">
              <form
                onSubmit={onContactSubmit}
                data-testid="contact-form"
                className="grid grid-cols-1 gap-6 sm:grid-cols-2"
              >
                <div className="sm:col-span-1">
                  <Label htmlFor="c-name">{t("contact.form.name")}</Label>
                  <input
                    id="c-name"
                    data-testid="contact-name"
                    className={`${fieldBase} text-sand placeholder:text-sand/40 border-sand/25 focus:border-terracotta`}
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    required
                  />
                </div>
                <div className="sm:col-span-1">
                  <Label htmlFor="c-email">{t("contact.form.email")}</Label>
                  <input
                    id="c-email"
                    type="email"
                    data-testid="contact-email"
                    className={`${fieldBase} text-sand placeholder:text-sand/40 border-sand/25 focus:border-terracotta`}
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    required
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="c-phone">{t("contact.form.phone")}</Label>
                  <input
                    id="c-phone"
                    data-testid="contact-phone"
                    className={`${fieldBase} text-sand placeholder:text-sand/40 border-sand/25 focus:border-terracotta`}
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="c-message">{t("contact.form.message")}</Label>
                  <textarea
                    id="c-message"
                    rows={4}
                    data-testid="contact-message"
                    className={`${fieldBase} resize-none text-sand placeholder:text-sand/40 border-sand/25 focus:border-terracotta`}
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    required
                  />
                </div>

                <div className="sm:col-span-2 mt-2">
                  <button
                    type="submit"
                    disabled={contactLoading}
                    data-testid="contact-submit"
                    className="inline-flex items-center gap-3 rounded-sm border border-terracotta bg-terracotta px-9 py-4 text-[11px] uppercase tracking-[0.26em] text-sand transition-all duration-300 hover:-translate-y-0.5 hover:border-sand hover:bg-sand hover:text-ocean disabled:opacity-60"
                  >
                    {contactLoading ? t("contact.form.sending") : t("contact.form.submit_message")}
                  </button>
                </div>
              </form>
            </TabsContent>

            {/* Booking form */}
            <TabsContent value="booking" className="mt-10">
              <form
                onSubmit={onBookingSubmit}
                data-testid="booking-form"
                className="grid grid-cols-1 gap-6 sm:grid-cols-2"
              >
                <div className="sm:col-span-1">
                  <Label htmlFor="b-name">{t("contact.form.name")}</Label>
                  <input
                    id="b-name"
                    data-testid="booking-name"
                    className={`${fieldBase} text-sand placeholder:text-sand/40 border-sand/25 focus:border-terracotta`}
                    value={bookingForm.name}
                    onChange={(e) => setBookingForm({ ...bookingForm, name: e.target.value })}
                    required
                  />
                </div>
                <div className="sm:col-span-1">
                  <Label htmlFor="b-email">{t("contact.form.email")}</Label>
                  <input
                    id="b-email"
                    type="email"
                    data-testid="booking-email"
                    className={`${fieldBase} text-sand placeholder:text-sand/40 border-sand/25 focus:border-terracotta`}
                    value={bookingForm.email}
                    onChange={(e) => setBookingForm({ ...bookingForm, email: e.target.value })}
                    required
                  />
                </div>
                <div className="sm:col-span-1">
                  <Label htmlFor="b-phone">{t("contact.form.phone")}</Label>
                  <input
                    id="b-phone"
                    data-testid="booking-phone"
                    className={`${fieldBase} text-sand placeholder:text-sand/40 border-sand/25 focus:border-terracotta`}
                    value={bookingForm.phone}
                    onChange={(e) => setBookingForm({ ...bookingForm, phone: e.target.value })}
                  />
                </div>

                <div className="sm:col-span-1">
                  <Label>{t("contact.form.session_type")}</Label>
                  <Select
                    value={bookingForm.session_type}
                    onValueChange={(v) => setBookingForm({ ...bookingForm, session_type: v })}
                  >
                    <SelectTrigger
                      data-testid="booking-session-type"
                      className="h-11 rounded-none border-0 border-b border-sand/25 bg-transparent px-0 text-[15px] text-sand shadow-none hover:border-terracotta focus:ring-0"
                    >
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent className="border border-warmBorder bg-sand text-ocean">
                      {sessionOptions.map((o) => (
                        <SelectItem key={o.value} value={o.value} data-testid={`booking-option-${o.value}`}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="sm:col-span-1">
                  <Label>{t("contact.form.preferred_date")}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid="booking-date-trigger"
                        className={`${fieldBase} flex items-center justify-between text-left text-sand placeholder:text-sand/40 border-sand/25 focus:border-terracotta`}
                      >
                        <span className={bookingDate ? "text-sand" : "text-sand/50"}>
                          {bookingDate ? format(bookingDate, "PPP") : "—"}
                        </span>
                        <CalendarIcon className="h-4 w-4 text-terracotta" strokeWidth={1.4} />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      className="w-auto border border-warmBorder bg-sand p-0 text-ocean"
                    >
                      <Calendar
                        mode="single"
                        selected={bookingDate}
                        onSelect={setBookingDate}
                        disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                        initialFocus
                        data-testid="booking-calendar"
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="sm:col-span-1">
                  <Label htmlFor="b-time">{t("contact.form.preferred_time")}</Label>
                  <input
                    id="b-time"
                    placeholder="ex: 17:00"
                    data-testid="booking-time"
                    className={`${fieldBase} text-sand placeholder:text-sand/40 border-sand/25 focus:border-terracotta`}
                    value={bookingForm.preferred_time}
                    onChange={(e) => setBookingForm({ ...bookingForm, preferred_time: e.target.value })}
                  />
                </div>

                <div className="sm:col-span-2">
                  <Label htmlFor="b-location">{t("contact.form.location")}</Label>
                  <input
                    id="b-location"
                    placeholder="Benagil, Praia da Marinha, Lagos…"
                    data-testid="booking-location"
                    className={`${fieldBase} text-sand placeholder:text-sand/40 border-sand/25 focus:border-terracotta`}
                    value={bookingForm.location}
                    onChange={(e) => setBookingForm({ ...bookingForm, location: e.target.value })}
                  />
                </div>

                <div className="sm:col-span-2">
                  <Label htmlFor="b-notes">{t("contact.form.notes")}</Label>
                  <textarea
                    id="b-notes"
                    rows={3}
                    data-testid="booking-notes"
                    className={`${fieldBase} resize-none text-sand placeholder:text-sand/40 border-sand/25 focus:border-terracotta`}
                    value={bookingForm.notes}
                    onChange={(e) => setBookingForm({ ...bookingForm, notes: e.target.value })}
                  />
                </div>

                <div className="sm:col-span-2 mt-2">
                  <button
                    type="submit"
                    disabled={bookingLoading}
                    data-testid="booking-submit"
                    className="inline-flex items-center gap-3 rounded-sm border border-terracotta bg-terracotta px-9 py-4 text-[11px] uppercase tracking-[0.26em] text-sand transition-all duration-300 hover:-translate-y-0.5 hover:border-sand hover:bg-sand hover:text-ocean disabled:opacity-60"
                  >
                    {bookingLoading ? t("contact.form.sending") : t("contact.form.submit_booking")}
                  </button>
                </div>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </section>
  );
}
